import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { PermitStep, PhotoRequirement } from "@ha/data/basic";
import { BaseModalComponent } from "@ha/ui/common";
import { Actions, ofActionSuccessful, Select, Store } from "@ngxs/store";

import { PwaState } from "@ha/util/pwa";
import { TranslocoService } from "@ngneat/transloco";
import { untilDestroyed } from "@ngneat/until-destroy";
import { take } from "rxjs";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { CommentDto, PermitCommentsDto } from "../../../permit-details/models/permit-comments-dto";
import { PermitDetailsActions } from "../../../permit-details/states/permit-details.action";
import { PermitDetailsSelectors } from "../../../permit-details/states/permit-details.selectors";
import { PermitPhoto } from "../../models/permit-photo.model";
import { PermitPhotoSelectors } from "../../states/permit-photo-selectors";
import { PermitPhotoActions } from "../../states/permit-photo.actions";

export interface PermitPhotoCommentModalComponentData {
    permitId: number;
    step: PermitStep;
    questionId?: number;
    modalType: "photo" | "comment";
}

@Component({
    selector: "pmt-permit-photo-comment-modal",
    templateUrl: "./permit-photo-comment-modal.component.html",
    styleUrls: ["./permit-photo-comment-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitPhotoCommentModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {
    @ViewChild("fileInput")
    public fileInput: ElementRef<HTMLInputElement>;

    @ViewChild("comment")
    public commentTextarea: ElementRef<HTMLTextAreaElement>;

    @Select(PermitPhotoSelectors.permitPhotos)
    public photos$: Observable<PermitPhoto[]>;

    @Select(PermitDetailsSelectors.comments)
    public comments$: Observable<PermitCommentsDto>;

    public photoRequirement = PhotoRequirement;
    public data: PermitPhotoCommentModalComponentData;
    public permitPhotosCommentForm: FormGroup;
    public savePermitPhotoAction = PermitPhotoActions.SavePermitPhoto;
    public createCommentAction = PermitDetailsActions.CreateComment;

    constructor(
        private store: Store,
        private cdr: ChangeDetectorRef,
        private actions$: Actions,
        private formBuilder: FormBuilder,
        private translocoService: TranslocoService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.actions$.pipe(ofActionSuccessful(PermitDetailsActions.CreateComment)).pipe(untilDestroyed(this), take(1))
            .subscribe(() => {
                this.closeModal();
                window.scrollTo({
                top: 0,
                behavior: "smooth",
                });
            });

        this.initForm();
    }

    public getCommentsWithAvatarId(comments: CommentDto[]): CommentDto[] {
        let avatarId = 0;

        const commentsWithAvatarId = comments.map((item) => Object.assign({}, item, { avatarId: undefined }));

        commentsWithAvatarId.forEach((comment, index, array) => {
            array[index].avatarId = <string> array.find((_) =>
                _.personId === comment.personId && _.avatarId !== undefined
            )?.avatarId ?? (++avatarId).toString();
        });

        return commentsWithAvatarId;
    }

    private initForm(): void {
        this.permitPhotosCommentForm = this.formBuilder.group({
            comment: ["", this.data.modalType === "comment" ? Validators.required : null],
            photos: [[]],
        });

        this.photos$.pipe(untilDestroyed(this)).subscribe((photos) => {
            this.permitPhotosCommentForm.patchValue({ photos: photos || [] }, { emitEvent: false });
            this.cdr.detectChanges();
        });

        this.comments$.pipe(untilDestroyed(this)).subscribe(() => {
            this.cdr.detectChanges();
        });
    }

    public ngAfterViewInit(): void {
        this.getScreenSize();
        this.setupCommentTextareaListener();
    }

    private setupCommentTextareaListener(): void {
        if (this.commentTextarea && this.commentTextarea.nativeElement) {
            this.commentTextarea.nativeElement.addEventListener("keydown", (event: KeyboardEvent) => {
                this.onCommentKeydown(event);
            });
        }
    }

    public onCommentKeydown(event: KeyboardEvent): void {
        if (event.key === "Enter") {
            if (event.shiftKey) {
                return;
            } else {
                event.preventDefault();
                this.savePhotoOrComment();
            }
        }
    }

    @HostListener("window:resize", ["$event"])
    public getScreenSize() {
        let width = 400;
        if (window.innerWidth < width) {
            width = window.innerWidth;
        }

        this.cdr.detectChanges();
    }

    public clear() {
        this.permitPhotosCommentForm.reset();
        // Clear the file input as well
        if (this.fileInput && this.fileInput.nativeElement) {
            this.fileInput.nativeElement.value = "";
        }
        // Trigger change detection after clearing
        this.cdr.detectChanges();
    }

    public async processSelectedImages(
        input: HTMLInputElement
        ): Promise<void> {
            const files = Array.from(input.files ?? []);

            for (const file of files) {
                const bytes = await file.arrayBuffer();

                // Creates an in-memory File instead of passing Safari's
                // problematic disk-backed File to XHR/fetch.
                const safeFile = new File([bytes], file.name, {
                type: file.type,
                lastModified: file.lastModified
                });

                this.store.dispatch(
                new PermitPhotoActions.SavePermitPhoto(
                    this.data.permitId,
                    safeFile,
                    this.data.step,
                    this.data.questionId
                )
            );
        }

        input.value = '';
    }   

    public removePhoto(guid: string): void {
        this.store.dispatch(new PermitPhotoActions.RemovePermitPhoto(guid)).pipe(take(1)).subscribe(() => {
            // Clear the file input after successfully removing the photo
            if (this.fileInput && this.fileInput.nativeElement) {
                this.fileInput.nativeElement.value = "";
            }
        });
    }

    public get isValid(): boolean {
        return this.permitPhotosCommentForm.valid;
    }

    public savePhotoOrComment() {
        if (this.isValid) {
            if (this.data.modalType === "photo") {
                this.closeModal();
                window.scrollTo({
                top: 0,
                behavior: "smooth",
                });
            } else {
                this.store.dispatch(
                    new PermitDetailsActions.CreateComment(
                        this.data.permitId,
                        this.permitPhotosCommentForm.get("comment").value,
                        this.data.step,
                        this.data.questionId,
                    ),
                );
            }
        }
    }

    public close() {
        this.closeModal();
        window.scrollTo({
        top: 0,
        behavior: "smooth",
        });
    }

    public get filteredPhotos$() {
        return this.photos$.pipe(map((photos) => {
            if (!photos) {
                return [];
            }
            if (this.data.questionId !== undefined && this.data.questionId !== null) {
                return photos.filter((photo) =>
                    photo.step === this.data.step && photo.questionId === this.data.questionId
                );
            }
            return photos.filter((photo) => photo.step === this.data.step);
        }));
    }

    public get filteredComments$() {
        return this.comments$.pipe(map((commentDto) => {
            if (!commentDto || !commentDto.comments) {
                return [];
            }
            if (this.data.questionId !== undefined && this.data.questionId !== null) {
                return commentDto.comments.filter((comment) =>
                    comment.step === this.data.step && comment.questionId === this.data.questionId
                );
            }
            return commentDto.comments.filter((comment) => comment.step === this.data.step);
        }));
    }

    public getPlaceholder(): string {
        const isMobile = this.store.selectSnapshot(PwaState.isMobile);
        return isMobile
            ? this.translocoService.translate("permit.photoCommentModal.commentPlaceholderMobile")
            : this.translocoService.translate("permit.photoCommentModal.commentPlaceholder");
    }
}
